import React, {useEffect, useState} from 'react';

import {Box} from 'src/design/atoms/Box';
import Parser from 'html-react-parser';
import type {CourseSessionCurrentContentResponse} from 'src/api/ProactApiClient';
import {VideoProviderTypeEnum} from 'src/api/ProactApiClient';
import {Subtitle} from 'src/design/atoms/Typography';
import {FormViewRow} from 'src/app/forms/FormViewRow/FormViewRow';
import {useAppSelector} from 'src/redux/hooks';
import {useIsTabActive} from 'src/shared/hooks/useIsTabActive';
import {fixRichTextTokens} from 'src/shared/utils/imageHandler';
import {useTimeout} from 'usehooks-ts';
import {InternalVideoPlayer} from '../video-players/InternalVideoPlayer';
import {VimeoVideoPlayer} from '../video-players/VimeoVideoPlayer';
import {GetSessionSubtitle} from './sessionHelpers';
import classes from './TrackerActiveSessionLesson.module.css';

export interface TrackerActiveSessionLessonProps {
  contentResponse: CourseSessionCurrentContentResponse;

  onVideoEnd: (seconds: number) => void;
  onVideoTimeEventUpdate?: (seconds: number) => void;
}
export const TrackerActiveSessionLesson: React.FC<
  TrackerActiveSessionLessonProps
> = ({contentResponse, onVideoEnd, onVideoTimeEventUpdate}) => {
  const {accessToken} = useAppSelector(state => state.auth);

  const {
    contentDefinition: content,
    currentContextIndex,
    totalContentLength,
  } = contentResponse;
  const title = GetSessionSubtitle(
    currentContextIndex,
    totalContentLength,
    content?.lesson?.headerText
  );
  const {
    bodyText: description,
    videoProviderType,
    videoUrl,
    videoId,
    enableVideoActiveScreenMonitoring,
    enableVideoAutostart,
    enableVideoControls,
    enableVideoResume,
  } = content!.lesson!;

  // If tab is inactive, we pause the video. Only do so if enableVideoActiveScreenMonitoring is true.
  const isTabActive = useIsTabActive();
  const [isVideoPaused, setIsVideoPaused] = useState(true);

  useTimeout(() => {
    setIsVideoPaused(
      enableVideoActiveScreenMonitoring === true && !isTabActive
    );
  }, 1000);

  useEffect(() => {
    setIsVideoPaused(
      enableVideoActiveScreenMonitoring === true && !isTabActive
    );
  }, [isTabActive]);

  return (
    <Box className={classes.lesson}>
      <Subtitle text={title} />
      {description && (
        <FormViewRow
          label="Description"
          value={Parser(fixRichTextTokens(description as string, accessToken))}
        />
      )}
      <Box className={classes.video}>
        {videoProviderType === VideoProviderTypeEnum.Vimeo && videoUrl && (
          <VimeoVideoPlayer
            videoUrl={videoUrl}
            enableVideoResume={enableVideoResume}
            enableVideoAutostart={enableVideoAutostart}
            isVideoPaused={isVideoPaused}
            currentProgressSeconds={
              contentResponse?.contentSession?.lesson?.currentProgressSeconds
            }
            onVideoTimeEventUpdate={onVideoTimeEventUpdate}
            onVideoEnd={onVideoEnd}
          />
        )}
        {videoProviderType === VideoProviderTypeEnum.Internal && videoId && (
          <InternalVideoPlayer
            videoId={videoId}
            enableVideoControls={enableVideoControls}
            enableVideoResume={enableVideoResume}
            enableVideoAutostart={enableVideoAutostart}
            isVideoPaused={isVideoPaused}
            currentProgressSeconds={
              contentResponse?.contentSession?.lesson?.currentProgressSeconds
            }
            onVideoTimeEventUpdate={onVideoTimeEventUpdate}
            onVideoEnd={onVideoEnd}
          />
        )}
      </Box>
    </Box>
  );
};
